/**
 * ================================================================
 * FirstCut - Generate More Modal
 * ================================================================
 * BEFORE MODIFYING: Read /app/memory/CONVENTIONS.md
 *
 * PURPOSE:
 * Quick content generation reusing existing focus context.
 * Two paths:
 * 1. "More of Same" - Generate new angles within same theme
 * 2. "Fresh Angle" - Opens full ContentFocusModal from Step 2
 *
 * KEY FEATURES:
 * - Reuses photo_context, source_image_url, visual_style from focus
 * - AI generates NEW angles while staying grounded in photo reality
 * - Avoids visual_directions already generated for this focus
 * ================================================================
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { FiX, FiLoader, FiImage, FiRefreshCw, FiZap, FiChevronDown, FiCheck } from 'react-icons/fi';
import PRICING_CONFIG from '../config/pricing';

const CREDIT_COSTS = PRICING_CONFIG.CREDIT_COSTS;

const IMAGE_RATIOS = [
  { id: '1:1', label: 'Square', desc: 'Posts' },
  { id: '4:5', label: 'Portrait', desc: 'Feed' },
  { id: '9:16', label: 'Vertical', desc: 'Reels/Stories' },
  { id: '16:9', label: 'Landscape', desc: 'YouTube' }
];

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', types: ['post'] },
  { id: 'facebook', label: 'Facebook', types: ['post'] },
  { id: 'linkedin', label: 'LinkedIn', types: ['post'] }
];

function GenerateMoreModal({ 
  focus, 
  campaign, 
  client,
  onClose, 
  onSuccess,
  onFreshAngle // Callback to open full ContentFocusModal at Step 2
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // Configuration
  const [quantity, setQuantity] = useState(3);
  const [selectedPlatforms, setSelectedPlatforms] = useState({ instagram: ['post'] });
  const [imageRatio, setImageRatio] = useState('1:1');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Existing content count (for display)
  const [existingCount, setExistingCount] = useState(0);
  
  // Credit calculation
  const [userCredits, setUserCredits] = useState({ remaining: 0, tier: 'free' });
  
  useEffect(() => {
    fetchUserCredits();
    fetchExistingCount();
  }, [focus?.id]);
  
  const fetchUserCredits = async () => {
    try {
      const usage = await api.getUsage();
      setUserCredits({
        remaining: usage.credits_remaining || 0,
        tier: usage.tier || 'free'
      });
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };
  
  const fetchExistingCount = async () => {
    if (!focus?.id) return;
    try {
      const response = await api.listContentIdeas({ focus_id: focus.id }, 1, 1);
      setExistingCount(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch existing count:', err);
    }
  };
  
  // Calculate total cost
  const calculateCost = () => {
    const platformCount = Object.values(selectedPlatforms).flat().length;
    const totalIdeas = platformCount * quantity;
    const costPerIdea = CREDIT_COSTS.generate_text + CREDIT_COSTS.generate_image;
    return totalIdeas * costPerIdea;
  };
  
  const totalCost = calculateCost();
  const canAfford = userCredits.remaining >= totalCost;
  
  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => {
      const newSelection = { ...prev };
      if (newSelection[platformId]) {
        delete newSelection[platformId];
      } else {
        newSelection[platformId] = ['post'];
      }
      return newSelection;
    });
  };
  
  // Handle Generate More
  const handleGenerate = async () => {
    if (!canAfford) {
      setError(`Not enough credits. Need ${totalCost}, have ${userCredits.remaining}`);
      return;
    }
    
    if (Object.keys(selectedPlatforms).length === 0) {
      setError('Select at least one platform');
      return;
    }
    
    setGenerating(true);
    setError('');
    setGenerationProgress(10);
    
    try {
      const request = {
        focus_id: focus.id,
        platforms: selectedPlatforms,
        quantity: quantity,
        mode: 'more_of_same',
        image_options: {
          ratio: imageRatio,
          quality: 'standard',
          individual_images: true,
          images_per_idea: 1
        }
      };
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 5, 90));
      }, 2000);
      
      const response = await api.generateMoreContent(request);
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      if (response.generated_count > 0) {
        setTimeout(() => {
          onSuccess?.(response.ideas);
          onClose();
        }, 500);
      } else {
        setError('No content was generated. The AI might need fresh direction.');
        setGenerating(false);
      }
    } catch (err) {
      console.error('Generation failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate content');
      setGenerating(false);
    }
  };
  
  // Render photo thumbnail if focus has source image
  const renderPhotoPreview = () => {
    if (!focus?.source_image_url) {
      return (
        <div className="w-16 h-16 bg-zinc-800 rounded-lg flex items-center justify-center">
          <FiImage className="w-6 h-6 text-zinc-500" />
        </div>
      );
    }
    
    return (
      <img 
        src={focus.source_image_url} 
        alt="Focus photo"
        className="w-16 h-16 object-cover rounded-lg border border-zinc-700"
      />
    );
  };
  
  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div>
            <h2 className="text-xl font-semibold text-white">Generate More</h2>
            <p className="text-sm text-zinc-400 mt-0.5">
              Create fresh content for "{focus?.name}"
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            data-testid="generate-more-close-btn"
          >
            <FiX className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Focus Context Display */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <div className="flex items-start gap-4">
              {renderPhotoPreview()}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-white">{focus?.name}</span>
                  {focus?.context_mode && (
                    <span className="text-xs px-2 py-0.5 bg-zinc-700 rounded-full text-zinc-300">
                      {focus.context_mode}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 line-clamp-2">
                  {focus?.description || focus?.visual_style}
                </p>
                {existingCount > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {existingCount} ideas already generated
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Path Selection */}
          <div className="grid grid-cols-2 gap-3">
            {/* More of Same - Active Path */}
            <div className="bg-emerald-500/10 border-2 border-emerald-500/50 rounded-xl p-4 cursor-default">
              <div className="flex items-center gap-2 mb-2">
                <FiRefreshCw className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-emerald-400">More of Same</span>
              </div>
              <p className="text-xs text-zinc-400">
                New angles, same theme & photo. AI stays creative within your reality.
              </p>
            </div>
            
            {/* Fresh Angle - Links to ContentFocusModal */}
            <button
              onClick={() => {
                onClose();
                onFreshAngle?.();
              }}
              className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 text-left hover:border-zinc-600 transition-colors"
              data-testid="fresh-angle-btn"
            >
              <div className="flex items-center gap-2 mb-2">
                <FiZap className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-zinc-300">Fresh Angle</span>
              </div>
              <p className="text-xs text-zinc-500">
                New direction entirely. Re-pick creative angle for same photo.
              </p>
            </button>
          </div>
          
          {/* Platform Selection */}
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 block">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => togglePlatform(platform.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPlatforms[platform.id]
                      ? 'bg-white text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                  data-testid={`platform-${platform.id}-btn`}
                >
                  {selectedPlatforms[platform.id] && <FiCheck className="inline w-3 h-3 mr-1" />}
                  {platform.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Quantity Selector */}
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 block">Ideas per platform</label>
            <div className="flex gap-2">
              {[1, 3, 5, 10].map(num => (
                <button
                  key={num}
                  onClick={() => setQuantity(num)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    quantity === num
                      ? 'bg-white text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                  data-testid={`quantity-${num}-btn`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
          
          {/* Advanced Options */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300"
          >
            <FiChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            Advanced options
          </button>
          
          {showAdvanced && (
            <div className="space-y-3 pl-4 border-l-2 border-zinc-800">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Image Ratio</label>
                <div className="flex flex-wrap gap-2">
                  {IMAGE_RATIOS.map(ratio => (
                    <button
                      key={ratio.id}
                      onClick={() => setImageRatio(ratio.id)}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        imageRatio === ratio.id
                          ? 'bg-zinc-700 text-white'
                          : 'bg-zinc-800/50 text-zinc-500 hover:bg-zinc-800'
                      }`}
                    >
                      {ratio.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-5 border-t border-zinc-800 bg-zinc-900/50">
          {generating ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Generating fresh content...</span>
                <span className="text-zinc-500">{generationProgress}%</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <p className="text-xs text-zinc-500 text-center">
                AI is being creative within your photo's reality...
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">
                  {Object.values(selectedPlatforms).flat().length * quantity} ideas
                </p>
                <p className={`text-xs ${canAfford ? 'text-zinc-500' : 'text-red-400'}`}>
                  {totalCost} credits ({userCredits.remaining} available)
                </p>
              </div>
              <button
                onClick={handleGenerate}
                disabled={!canAfford || Object.keys(selectedPlatforms).length === 0}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
                  canAfford && Object.keys(selectedPlatforms).length > 0
                    ? 'bg-white text-black hover:bg-zinc-200'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid="generate-more-submit-btn"
              >
                <FiZap className="inline w-4 h-4 mr-2" />
                Generate
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  return createPortal(modalContent, document.body);
}

export default GenerateMoreModal;
